import { NavLink, useLocation, Link, useNavigate } from "react-router-dom";
import { useEffect, useState } from "react";
import { toast } from "sonner";
import { SquaresFour, Megaphone, ImageSquare, UsersThree, Target, SignOut, Plugs, Robot, Brain, PaintBrush, Swatches, CreditCard, Warning, LockKey, Lifebuoy, ClipboardText, ListChecks, ChartBar, CaretDown, ClockCounterClockwise, Moon, PaperPlaneRight, RocketLaunch } from "@phosphor-icons/react";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import AlertsBell from "@/components/AlertsBell";
import { BrandMark } from "@/components/BrandMark";

const NAV_GROUPS = [
  { key: "top", label: null, items: [
    { to: "/", label: "Overview", icon: SquaresFour, testId: "nav-overview" }] },
  { key: "ads", label: "Advertising", items: [
    { to: "/campaigns", label: "Campaigns", icon: Megaphone, testId: "nav-campaigns" },
    { to: "/audiences", label: "Audiences", icon: Target, testId: "nav-audiences" },
    { to: "/ads", label: "Ads", icon: ImageSquare, testId: "nav-ads" }] },
  { key: "leads", label: "Leads", items: [
    { to: "/leads", label: "Leads Inbox", icon: UsersThree, testId: "nav-leads" },
    { to: "/forms", label: "Lead Forms", icon: ClipboardText, testId: "nav-forms" },
    { to: "/assessment", label: "Assessment", icon: ListChecks, testId: "nav-assessment" },
    { to: "/conversion", label: "Conversion Report", icon: ChartBar, testId: "nav-conversion" }] },
  { key: "ai", label: "AI Studio", items: [
    { to: "/automation", label: "AI Agent", icon: Robot, testId: "nav-automation" },
    { to: "/team", label: "Creative Team", icon: PaintBrush, testId: "nav-team" },
    { to: "/brand", label: "Brand Kit", icon: Swatches, testId: "nav-brand" },
    { to: "/insights", label: "Insights", icon: Brain, testId: "nav-insights" }] },
  { key: "settings", label: "Settings", items: [
    { to: "/integrations", label: "Integrations", icon: Plugs, testId: "nav-integrations" },
    { to: "/billing", label: "Billing", icon: CreditCard, testId: "nav-billing" },
    { to: "/team-members", label: "Team Members", icon: UsersThree, testId: "nav-team-members", ownerOnly: true },
    { to: "/activity", label: "Activity Log", icon: ClockCounterClockwise, testId: "nav-activity", ownerOnly: true },
    { to: "/roadmap", label: "Product Roadmap", icon: RocketLaunch, testId: "nav-roadmap" },
    { to: "/support", label: "Support", icon: Lifebuoy, testId: "nav-support" }] },
];

const PATH_MODULE = { "/": "overview", "/campaigns": "campaigns", "/audiences": "audiences", "/ads": "ads",
  "/leads": "leads", "/forms": "lead_forms", "/assessment": "assessment", "/conversion": "conversion_report",
  "/automation": "ai_agent", "/team": "creative_team", "/brand": "brand_kit", "/insights": "insights",
  "/integrations": "integrations", "/connect": "integrations", "/billing": "billing" };

const TITLES = { "/": "Portfolio Overview", "/campaigns": "Campaigns", "/audiences": "Audiences", "/ads": "Ads — Audit", "/leads": "Leads Inbox", "/assessment": "Lead Assessment", "/conversion": "Conversion Report", "/forms": "Lead Forms", "/carousel": "Carousel Ads", "/automation": "AI Agent", "/team": "AI Creative Team", "/brand": "Brand Kit", "/insights": "AI Insights History", "/billing": "Billing & Plans", "/support": "Support", "/connect": "Connect Meta", "/integrations": "Integrations", "/team-members": "Team Members", "/activity": "Activity Log", "/roadmap": "Product Roadmap" };

const LockScreen = ({ dormant, reactivationPending }) => {
  const [sent, setSent] = useState(!!reactivationPending);
  const requestReactivation = async () => {
    try {
      await api.post("/billing/reactivation-request");
      setSent(true);
      toast.success("Request sent — our team will review it shortly");
    } catch {
      toast.error("Could not send the request — please try again");
    }
  };
  if (dormant)
    return (
      <div className="min-h-[60vh] flex items-center justify-center" data-testid="dormant-lock-screen">
        <div className="max-w-md text-center bg-white border border-[#E5E7EB] rounded-xl p-10">
          <span className="mx-auto h-14 w-14 rounded-full bg-amber-50 flex items-center justify-center">
            <Moon size={26} weight="duotone" className="text-amber-500" />
          </span>
          <h2 className="mt-5 font-heading text-xl font-extrabold tracking-tight text-[#0A0D14]">Account dormant</h2>
          <p className="mt-2 text-sm text-[#525866] leading-relaxed">
            This free workspace had no sign-ins for over 90 days, so it was paused. Your data is safe — request
            reactivation and our team will restore access.
          </p>
          {sent ? (
            <div className="mt-6 inline-flex items-center gap-2 rounded-full bg-emerald-50 border border-emerald-200 text-emerald-700 font-bold text-sm px-6 py-3" data-testid="reactivation-sent-badge">
              ✓ Request sent — awaiting approval
            </div>
          ) : (
            <button onClick={requestReactivation} data-testid="request-reactivation-btn"
              className="mt-6 inline-flex items-center gap-2 rounded-full bg-[#FF6B35] hover:bg-[#E85A25] text-white font-bold text-sm px-6 py-3 transition-transform hover:-translate-y-[1px] active:scale-95">
              <PaperPlaneRight size={16} weight="duotone" /> Request reactivation
            </button>
          )}
        </div>
      </div>
    );
  return (
    <div className="min-h-[60vh] flex items-center justify-center" data-testid="workspace-lock-screen">
      <div className="max-w-md text-center bg-white border border-[#E5E7EB] rounded-xl p-10">
        <span className="mx-auto h-14 w-14 rounded-full bg-red-50 flex items-center justify-center">
          <LockKey size={26} weight="duotone" className="text-red-500" />
        </span>
        <h2 className="mt-5 font-heading text-xl font-extrabold tracking-tight text-[#0A0D14]">Workspace locked</h2>
        <p className="mt-2 text-sm text-[#525866] leading-relaxed">
          Your plan expired and the 15-day renewal window has passed. Your data is safe — renew your plan to restore full access.
        </p>
        <Link to="/billing" data-testid="lock-screen-renew-btn"
          className="mt-6 inline-flex items-center gap-2 rounded-full bg-[#FF6B35] hover:bg-[#E85A25] text-white font-bold text-sm px-6 py-3 transition-transform hover:-translate-y-[1px] active:scale-95">
          <CreditCard size={16} weight="duotone" /> Renew plan
        </Link>
      </div>
    </div>
  );
};

export default function Layout({ children }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const [meta, setMeta] = useState(null);
  const [billing, setBilling] = useState(null);
  const [collapsed, setCollapsed] = useState(() => {
    try { return JSON.parse(localStorage.getItem("nav_collapsed") || "[]"); } catch { return []; }
  });
  const initials = (user?.name || "U").split(" ").map((s) => s[0]).slice(0, 2).join("").toUpperCase();

  const toggleGroup = (key) => {
    setCollapsed((c) => {
      const next = c.includes(key) ? c.filter((k) => k !== key) : [...c, key];
      localStorage.setItem("nav_collapsed", JSON.stringify(next));
      return next;
    });
  };

  useEffect(() => {
    api.get("/meta/status").then(({ data }) => setMeta(data)).catch(() => {});
    api.get("/billing/status").then(({ data }) => setBilling(data)).catch(() => {});
  }, [pathname]);

  const bstate = billing?.billing_state;
  const locked = bstate?.status === "locked";
  const grace = bstate?.status === "grace";
  const expiringSoon = bstate?.status === "active" && bstate?.expires_at && bstate?.days_left != null && bstate.days_left <= 3;

  const live = meta?.connected;

  const metaExpDays = live && meta?.token_expires_at
    ? Math.ceil((new Date(meta.token_expires_at) - Date.now()) / 86400000) : null;
  const metaExpired = metaExpDays != null && metaExpDays <= 0;
  const metaExpiring = metaExpDays != null && metaExpDays > 0 && metaExpDays <= 10;
  const [expiryDismissed, setExpiryDismissed] = useState(() => {
    const until = Number(localStorage.getItem("meta_expiry_dismissed_until") || 0);
    return Date.now() < until;
  });
  const dismissExpiry = () => {
    localStorage.setItem("meta_expiry_dismissed_until", String(Date.now() + 86400000));
    setExpiryDismissed(true);
  };
  const showMetaBanner = metaExpired || (metaExpiring && !expiryDismissed);

  const syncFailed = live && meta?.last_attempt_ok === false;
  const failingHours = syncFailed && meta?.failing_since ? (Date.now() - new Date(meta.failing_since)) / 3600000 : 0;
  const syncCritical = failingHours >= 6;
  const fmtT = (iso) => (iso ? new Date(iso).toLocaleString("en-IN", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" }) : "—");

  const isOwner = user?.role === "owner" || user?.role === "admin";
  const userMods = new Set(user?.modules || []);
  const canSee = (item) => {
    if (item.ownerOnly) return isOwner;
    if (isOwner || item.to === "/support") return true;
    const mod = PATH_MODULE[item.to];
    return !mod || userMods.has(mod);
  };
  const navGroups = NAV_GROUPS.map((g) => ({ ...g, items: g.items.filter(canSee) })).filter((g) => g.items.length > 0);

  useEffect(() => {
    if (!user || isOwner) return;
    const mod = PATH_MODULE[pathname];
    if ((mod && !userMods.has(mod)) || pathname === "/team-members") {
      const first = navGroups.flatMap((g) => g.items).find((i) => i.to !== "/support");
      navigate(first ? first.to : "/support", { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pathname, user]);

  return (
    <div className="min-h-screen bg-[#FAFAFB] flex">
      <aside className="w-60 shrink-0 border-r border-[#E5E7EB] bg-white flex flex-col fixed inset-y-0 z-30">
        <div className="px-5 py-6">
          <BrandMark size={30} />
        </div>
        <nav className="px-3 flex-1 overflow-y-auto pb-2">
          {navGroups.map((g) => {
            const hasActive = g.items.some((i) => (i.to === "/" ? pathname === "/" : pathname.startsWith(i.to)));
            const isCollapsed = collapsed.includes(g.key) && !hasActive;
            return (
              <div key={g.key} className="mb-1">
                {g.label && (
                  <button onClick={() => toggleGroup(g.key)} data-testid={`nav-group-${g.key}`}
                    className="w-full flex items-center justify-between px-3 pt-3 pb-1 text-[10px] font-bold uppercase tracking-[0.16em] text-[#8a90a0] hover:text-[#0A0D14] transition-colors">
                    {g.label}
                    <CaretDown size={11} weight="bold" className={`transition-transform ${isCollapsed ? "-rotate-90" : ""}`} />
                  </button>
                )}
                {!isCollapsed && (
                  <div className="space-y-0.5">
                    {g.items.map(({ to, label, icon: Icon, testId }) => (
                      <NavLink key={to} to={to} end={to === "/"} data-testid={testId}
                        className={({ isActive }) =>
                          `flex items-center gap-3 px-3 py-2 rounded-lg text-[13px] font-semibold transition-colors ${
                            isActive ? "bg-[#0A0D14] text-white" : "text-[#525866] hover:bg-[#F3F4F6] hover:text-[#0A0D14]"
                          }`}>
                        <Icon size={17} weight="duotone" />
                        {label}
                      </NavLink>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </nav>
        <div className="px-3 pb-3">
          <div className="rounded-lg border border-[#E5E7EB] bg-[#FAFAFB] p-3 mb-3" data-testid="sidebar-meta-status">
            <div className="flex items-center gap-2 text-xs font-bold text-[#0A0D14]">
              <Plugs size={14} weight="duotone" className={live ? "text-success" : "text-warning"} />
              {live ? `${meta.account_count} Live Ad Accounts` : "Not connected"}
            </div>
            <p className="text-[11px] text-[#8a90a0] mt-1 leading-snug">{live ? "Connected to Meta Marketing API" : "Connect Meta to pull your live ads data"}</p>
          </div>
          <div className="flex items-center gap-2.5 px-2 py-2">
            <span className="h-8 w-8 rounded-full bg-[#0052FF] text-white text-xs font-bold flex items-center justify-center">{initials}</span>
            <div className="flex-1 min-w-0">
              <div className="text-sm font-semibold text-[#0A0D14] truncate" data-testid="user-name">{user?.name}</div>
              <div className="text-[11px] text-[#8a90a0] truncate" data-testid="workspace-name" title={user?.email}>{user?.workspace_name || user?.email}</div>
            </div>
            <button onClick={logout} data-testid="logout-button" title="Log out"
              className="text-[#8a90a0] hover:text-red-500 transition-colors p-1.5 rounded-md hover:bg-red-50">
              <SignOut size={17} />
            </button>
          </div>
        </div>
      </aside>
      <div className="flex-1 ml-60 min-w-0">
        <header className="sticky top-0 z-20 bg-white/80 backdrop-blur-xl border-b border-[#E5E7EB]/80 px-8 py-4 flex items-center justify-between">
          <h1 className="font-heading text-xl font-extrabold tracking-tight text-[#0A0D14]" data-testid="page-title">{TITLES[pathname] || "5ads.ai"}</h1>
          <div className="flex items-center gap-3">
            <AlertsBell refreshKey={pathname} />
            <span className={`inline-flex items-center gap-1.5 rounded-full border px-3 py-1 text-[11px] font-bold ${
              live ? "border-success/30 bg-success/5 text-[#00953e]" : "border-warning/30 bg-warning/5 text-[#8a6400]"
            }`} data-testid="header-data-source-badge">
              <span className={`h-1.5 w-1.5 rounded-full animate-pulse ${live ? "bg-success" : "bg-warning"}`} />
              {live ? "Live · Meta Marketing API" : "Not connected · Connect Meta"}
            </span>
          </div>
        </header>
        {(grace || expiringSoon) && (
          <div data-testid="billing-banner"
            className={`px-8 py-2.5 flex flex-wrap items-center gap-2 text-xs font-bold border-b ${
              grace ? "bg-red-50 border-red-200 text-red-700" : "bg-amber-50 border-amber-200 text-amber-800"}`}>
            <Warning size={15} weight="fill" className="shrink-0" />
            {grace
              ? `Your ${billing.plan} plan expired — workspace is read-only. Renew within ${bstate.days_left} day${bstate.days_left === 1 ? "" : "s"} or your account will be locked.`
              : `Your ${billing.plan} plan expires ${bstate.days_left === 0 ? "today" : `in ${bstate.days_left} day${bstate.days_left === 1 ? "" : "s"}`} — renew in advance to avoid interruption.`}
            <Link to="/billing" data-testid="billing-banner-renew" className="underline underline-offset-2 hover:opacity-80">Renew now</Link>
          </div>
        )}
        {showMetaBanner && (
          <div data-testid="meta-expiry-banner"
            className={`px-8 py-2.5 flex flex-wrap items-center gap-2 text-xs font-bold border-b ${
              metaExpired ? "bg-red-50 border-red-200 text-red-700" : "bg-amber-50 border-amber-200 text-amber-800"}`}>
            <Warning size={15} weight="fill" className="shrink-0" />
            {metaExpired
              ? "Your Meta connection has expired — data is frozen at the last sync. Re-connect to resume campaigns, leads and automations."
              : `Your Meta connection expires in ${metaExpDays} day${metaExpDays === 1 ? "" : "s"}. When it lapses, syncing, new leads and WhatsApp auto-replies stop silently.`}
            <Link to="/connect" data-testid="meta-expiry-reconnect" className="underline underline-offset-2 hover:opacity-80">Re-connect with Facebook</Link>
            {!metaExpired && (
              <button onClick={dismissExpiry} data-testid="meta-expiry-dismiss" className="ml-auto text-[11px] font-bold opacity-60 hover:opacity-100">Dismiss for 24h</button>
            )}
          </div>
        )}
        {syncFailed && (
          <div data-testid="sync-health-banner"
            className={`px-8 py-2.5 flex flex-wrap items-center gap-2 text-xs font-bold border-b ${
              syncCritical ? "bg-red-50 border-red-200 text-red-700" : "bg-amber-50 border-amber-200 text-amber-800"}`}>
            <Warning size={15} weight="fill" className="shrink-0" />
            {syncCritical
              ? `Meta sync has been failing for ${Math.floor(failingHours)}+ hours — data is frozen at your last successful sync (${fmtT(meta?.last_sync)}). Check your Meta connection.`
              : `Meta was unreachable at ${fmtT(meta?.last_attempt_at)} — showing data from your last successful sync (${fmtT(meta?.last_sync)}). Retrying automatically every 15 minutes.`}
            {syncCritical && (
              <Link to="/connect" data-testid="sync-health-reconnect" className="underline underline-offset-2 hover:opacity-80">Check connection</Link>
            )}
          </div>
        )}
        <main className="px-8 py-8 max-w-[1400px]">{locked && pathname !== "/billing" && pathname !== "/support" ? <LockScreen dormant={!!bstate?.dormant} reactivationPending={!!billing?.reactivation_pending} /> : children}</main>
      </div>
    </div>
  );
}
